import { Component, Inject, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { User } from '@core/interfaces';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { Subject } from 'rxjs';

import { UsersService } from '../../services/users-resolver.service';

import { ChangeAvatarService } from '@core/components/change-avatar/change-avatar.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { MessageService } from '@core/errors/messages-service';
import { UploadService } from '@core/services/upload.service';
import { getErrorCode } from '@core/utils/get-error-code';
import { DialogArchiveOrUnarchiveUserComponent } from '../dialog-archive-or-unarchive-user/dialog-archive-or-unarchive-user.component';
import { TabGeneralComponent } from './tabs/general/tab-general.component';

import { takeUntil } from 'rxjs/operators';
import english from '../../i18n/en.json';
import spanish from '../../i18n/es.json';
import portuguese from '../../i18n/pt.json';

@Component({
  selector: 'dialog-users',
  templateUrl: './dialog-users.component.html',
  styleUrls: ['./dialog-users.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogUsersComponent implements OnInit, OnDestroy {
  public userForm: FormGroup;

  public user: User;
  public currentUser: User;
  public isViewMode: boolean;
  public selectedTabIndex = 0;
  public hasLinkedGroups: boolean;
  public isLoading = false;
  private isOnEditDialog: boolean;
  private readonly unsubscribeAll = new Subject<void>();

  @Output() reloadPage: Subject<void> = new Subject<void>();
  @ViewChild(TabGeneralComponent, { static: true }) tabGeneralComponent: TabGeneralComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly dialogData: any,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    public readonly matDialogRef: MatDialogRef<DialogUsersComponent>,
    private readonly customMessageService: CustomMessagesService,
    private readonly changeAvatarService: ChangeAvatarService,
    private fuseProgressBarService: FuseProgressBarService,
    private readonly imageUploadService: UploadService,
    private messageService: MessageService,
    private usersService: UsersService,
    private materialDialog: MatDialog,
    private fb: FormBuilder
  ) {
    this.user = this.dialogData.user;
    this.isViewMode = this.dialogData.isViewMode;
    this.currentUser = this.dialogData.currentUser;
    this.reloadPage = this.dialogData.reloadPage;
    this.isOnEditDialog = !!(this.user.id);
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  public ngOnInit(): void {
    this.userForm = this.fb.group({
      name: [this.user.name, [Validators.required]],
      type: [this.user.type, [Validators.required]],
      role: [this.user.role, [Validators.required]],
      loginIntegrationEnabled: [this.user.loginIntegrationEnabled || false, [Validators.required]],
      loginProvider: [this.user.loginProvider || 'email-password', [Validators.required]],
      email: [{ value: this.user.email, disabled: !!this.user.id }, [Validators.required, Validators.email]],
      groups: new FormArray((this.user.groups || []).map((group) => this.fb.group(group))),
      preferences: this.fb.group({
        featureOpenedMaintenanceSendEmailNotification: new FormControl(
          this.user.preferences ? this.user.preferences.featureOpenedMaintenanceSendEmailNotification : false
        )
      })
    });

    const groupsFormArray = this.userForm.get('groups') as FormArray;
    if (groupsFormArray.value.length) {
      this.hasLinkedGroups = true;
    }

    groupsFormArray.valueChanges.pipe(takeUntil(this.unsubscribeAll)).subscribe((value) => {
      if (value.length) {
        this.hasLinkedGroups = true;
      } else {
        this.hasLinkedGroups = false;
      }
    });

    if (this.isViewMode) {
      this.userForm.disable();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  public async openArchiveOrUnarchiveUserDialog(archive: boolean): Promise<void> {
    await this.materialDialog
      .open<any>(DialogArchiveOrUnarchiveUserComponent, {
        panelClass: 'dialog-archive-or-unarchive-user',
        data: {
          userId: this.user.id,
          isArchived: archive,
          isLoading: this.isLoading
        }
      }).afterClosed().subscribe((value) => {
        if (value && value.reload) {
          this.reloadPage.next();
          this.matDialogRef.close({ reload: true });
        }
      });
  }

  public continue(): void {
    this.selectedTabIndex = 1;
  }

  public async saveUser(): Promise<void> {
    this.fuseProgressBarService.show();
    this.isLoading = true;
    this.matDialogRef.disableClose = true;

    const input = this.userForm.value;
    input.groups = this.userForm.get('groups').value.map((group) => group.id);
    input.loginIntegrationEnabled = ['active-directory', 'google'].includes(input.loginProvider);

    try {
      const savedUser = await this.usersService.save(this.user.id, input).toPromise();

      if (!this.isOnEditDialog) {
        await this.saveNewUser(savedUser);
      }

      this.customMessageService.success('USERS.SUCCESS_MESSAGE');
      this.matDialogRef.close({ reload: true });
      this.reloadPage.next();
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'user_email_duplicated') {
        this.selectedTabIndex = 0;
        this.userForm.get('email').setErrors({
          'user_email_duplicated': true
        });

        return;
      }

      if (code === 'login_via_integration_not_configured') {
        this.selectedTabIndex = 0;
        this.userForm.get('loginIntegrationEnabled').setErrors({
          'login_via_integration_not_configured': true
        });
        this.customMessageService.error('USERS.INTEGRATION_NOT_CONFIGURED');
        return;
      }

      this.messageService.error(err);
      this.customMessageService.error('USERS.ERROR_MESSAGE');
    } finally {
      this.matDialogRef.disableClose = false;
      this.fuseProgressBarService.hide();
      this.isLoading = false;
    }
  }

  private async saveNewUser(savedUser): Promise<void> {
    const avatarImage = this.tabGeneralComponent.avatarImagePayload;

    if (avatarImage) {
      this.tabGeneralComponent.avatarImagePayload.path = `users/${savedUser.id}`;
      const { path: avatarUrl } = await this.imageUploadService.upload(this.tabGeneralComponent.avatarImagePayload);
      await this.changeAvatarService.updateUser({
        id: savedUser.id,
        input: { avatarUrl }
      });
    }

    if (this.user['password']) {
      await this.usersService.setUserPassword(savedUser.id, {
        password: this.user['password'],
        permanentPassword: false
      }).toPromise();
    }
  }
}
